/**
 * Route Protection Middleware
 *
 * This module provides route-level security components and utilities
 * for protecting pages and routes based on user permissions.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AppPermission, ROUTE_PERMISSIONS } from "@/types/auth";
import { useAppAuth } from "@/contexts/AuthContext";
import { PlanLockoutGuard } from "./PlanLockoutGuard";

interface RouteGuardProps {
  children: React.ReactNode;
  requiredPermission?: AppPermission;
  requiredPermissions?: AppPermission[];
  fallbackPath?: string;
  requireAny?: boolean; // If true, user needs ANY of the permissions; if false, ALL permissions
}

/**
 * Generic route guard component that protects routes based on permissions
 */
export function RouteGuard({
  children,
  requiredPermission,
  requiredPermissions,
  fallbackPath = "/unauthorized",
  requireAny = true,
}: RouteGuardProps) {
  const { isAuthenticated, isLoading, hasPermission, hasAnyPermission } = useAppAuth();
  const location = useLocation();

  // Show loading while auth is being determined
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/sign-in" state={{ from: location }} replace />;
  }

  // Check permissions if specified
  const permissions = requiredPermissions || (requiredPermission ? [requiredPermission] : []);

  if (permissions.length > 0) {
    const hasAccess = requireAny
      ? hasAnyPermission(permissions)
      : permissions.every((permission) => hasPermission(permission));

    if (!hasAccess) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Create a route guard for a specific permission
 */
export function createRouteGuard(requiredPermission: AppPermission, fallbackPath?: string) {
  return function PermissionRouteGuard({ children }: { children: React.ReactNode }) {
    return (
      <RouteGuard requiredPermission={requiredPermission} fallbackPath={fallbackPath}>
        {children}
      </RouteGuard>
    );
  };
}

/**
 * Create a route guard for multiple permissions
 */
export function createMultiPermissionRouteGuard(
  requiredPermissions: AppPermission[],
  requireAny: boolean = true,
  fallbackPath?: string
) {
  return function MultiPermissionRouteGuard({ children }: { children: React.ReactNode }) {
    return (
      <RouteGuard
        requiredPermissions={requiredPermissions}
        requireAny={requireAny}
        fallbackPath={fallbackPath}
      >
        {children}
      </RouteGuard>
    );
  };
}

/**
 * Auto route guard that uses ROUTE_PERMISSIONS mapping
 */
export function AutoRouteGuard({ children }: { children: React.ReactNode }) {
  const { canAccessRoute } = useAppAuth();
  const location = useLocation();

  const hasAccess = canAccessRoute(location.pathname);

  if (!hasAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

// Pre-built route guards for common permissions
export const AdminRouteGuard = createRouteGuard("settings:write");
export const ManagerRouteGuard = createMultiPermissionRouteGuard(["team:write"], true);
export const AnalyticsRouteGuard = createRouteGuard("analytics:read");
export const TeamManagementRouteGuard = createRouteGuard("team:write");
export const DocumentManagementRouteGuard = createRouteGuard("documents:write");

/**
 * Higher-order component for protecting routes
 */
export function withRouteProtection<P extends object>(
  Component: React.ComponentType<P>,
  requiredPermission: AppPermission,
  fallbackPath?: string
) {
  return function ProtectedComponent(props: P) {
    return (
      <RouteGuard requiredPermission={requiredPermission} fallbackPath={fallbackPath}>
        <Component {...props} />
      </RouteGuard>
    );
  };
}

/**
 * Hook for checking route access in components
 */
export function useRouteAccess(route?: string) {
  const { canAccessRoute, hasPermission } = useAppAuth();
  const location = useLocation();

  const currentRoute = route || location.pathname;
  const hasAccess = canAccessRoute(currentRoute);

  const checkRoutePermission = (permission: AppPermission): boolean => {
    return hasPermission(permission);
  };

  const getRequiredPermissions = (routePath: string): AppPermission[] => {
    return ROUTE_PERMISSIONS[routePath] || [];
  };

  return {
    hasAccess,
    currentRoute,
    checkRoutePermission,
    getRequiredPermissions,
    requiredPermissions: getRequiredPermissions(currentRoute),
  };
}

/**
 * Component for displaying unauthorized access message
 */
export function UnauthorizedAccess() {
  const { appRole, permissions, isOwner } = useAppAuth();
  const location = useLocation();
  const requiredPermissions = ROUTE_PERMISSIONS[location.pathname] || [];

  // Provide helpful guidance based on the user's role
  const getGuidanceMessage = () => {
    if (isOwner) {
      return "As an owner, you should have access to all features. Please contact support if this issue persists.";
    }

    if (appRole === "admin") {
      return "This feature requires additional permissions. Contact your organization owner to adjust your access level.";
    }

    if (appRole === "manager") {
      return "This feature is restricted to administrators. Contact your organization admin or owner for access.";
    }

    return "This feature requires elevated permissions. Contact your manager or administrator for access.";
  };

  const getSuggestedActions = () => {
    const actions = [
      { label: "Go to Dashboard", href: "/dashboard" },
      { label: "View Available Features", href: "/dashboard" },
    ];

    // Add role-specific suggestions
    if (appRole === "member") {
      actions.push({ label: "View Leads", href: "/leads" });
    }

    if (appRole === "manager" || appRole === "admin") {
      actions.push({ label: "Manage Team", href: "/employees" });
    }

    return actions;
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-lg rounded-lg bg-white p-8 shadow-lg">
        <div className="text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg
              className="h-8 w-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>

          <h2 className="mt-4 text-xl font-semibold text-gray-900">Access Restricted</h2>
          <p className="mt-2 text-gray-600">
            You don't have the required permissions to access this page.
          </p>

          <div className="mt-4 rounded-lg bg-blue-50 p-4">
            <p className="text-sm text-blue-800">{getGuidanceMessage()}</p>
          </div>

          {process.env.NODE_ENV === "development" && (
            <div className="mt-4 rounded bg-gray-100 p-3 text-left text-xs">
              <p>
                <strong>Your Role:</strong> {appRole}
              </p>
              <p>
                <strong>Is Owner:</strong> {isOwner ? "Yes" : "No"}
              </p>
              <p>
                <strong>Your Permissions:</strong>{" "}
                {permissions.length > 0 ? permissions.join(", ") : "None"}
              </p>
              <p>
                <strong>Required:</strong>{" "}
                {requiredPermissions.length > 0 ? requiredPermissions.join(", ") : "Unknown"}
              </p>
              <p>
                <strong>Current Path:</strong> {location.pathname}
              </p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            <div className="flex flex-col justify-center gap-3 sm:flex-row">
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Go Back
              </button>
              <a
                href="/dashboard"
                className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Go to Dashboard
              </a>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-sm text-gray-500">You can access:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {getSuggestedActions().map((action, index) => (
                  <a
                    key={index}
                    href={action.href}
                    className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-800 hover:bg-gray-200"
                  >
                    {action.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Enhanced protected route component that replaces the basic ProtectedRoute
 */
export function ProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requireAny = true,
}: {
  children: React.ReactNode;
  requiredPermission?: AppPermission;
  requiredPermissions?: AppPermission[];
  requireAny?: boolean;
}) {
  return (
    <PlanLockoutGuard>
      <RouteGuard
        requiredPermission={requiredPermission}
        requiredPermissions={requiredPermissions}
        requireAny={requireAny}
        fallbackPath="/unauthorized"
      >
        {children}
      </RouteGuard>
    </PlanLockoutGuard>
  );
}
